// Derives the combined display status for a milestone: lifecycle state plus
// an optional risk overlay (late, delivered-late, due-soon). Mirrors the
// backend deriveMilestoneDisplayStatus.ts; any change here must be applied
// to both files simultaneously. Parity is covered by the shared fixtures in
// `tests/shared/milestoneLifecycleFixtures/` (see that directory's README),
// consumed by `deriveMilestoneStatus.parity.test.ts` on both stacks.

import {
  deriveMilestoneLifecycleStatus,
  deriveLate,
  deriveOverdue,
  type MilestoneDerivedStatus,
} from "./deriveMilestoneStatus";
import { getBrisbaneToday } from "./getBrisbaneToday";
import { daysBetween } from "./isoDay";
import { isSet, type MilestoneInput } from "./milestoneInput";

export type MilestoneRiskOverlay = "late" | "delivered-late" | "due-soon";

export interface MilestoneDisplayStatusResult {
  lifecycle: MilestoneDerivedStatus;
  riskOverlay?: MilestoneRiskOverlay;
}

const DUE_SOON_DAYS = 14;

/**
 * Returns the lifecycle state plus the highest-priority risk overlay for
 * display surfaces. Risk overlay precedence (highest to lowest):
 *   1. 'delivered-late' — delivered after planned date. Preserved through
 *                         accepted AND paid so the historical slippage stays
 *                         visible — payment receipt does not erase the
 *                         contractual fact that the supplier delivered late.
 *   2. 'late'           — undelivered and planned date is past. Suppressed
 *                         once paid (no actionable signal once the milestone
 *                         is closed out, even if delivery was never recorded).
 *   3. 'due-soon'       — upcoming lifecycle, planned date within 14 days.
 *
 * Note: 'delivered-late' and 'late' are mutually exclusive at the data
 * level (one requires deliveredAt set, the other requires it unset), so
 * the precedence between them never resolves a real conflict.
 *
 * `today` defaults to Brisbane today; tests inject a stable value.
 */
export function deriveMilestoneDisplayStatus(
  m: MilestoneInput,
  today?: string,
): MilestoneDisplayStatusResult {
  const lifecycle = deriveMilestoneLifecycleStatus(m);

  if (deriveLate(m)) {
    return { lifecycle, riskOverlay: "delivered-late" };
  }

  if (lifecycle === "paid") {
    return { lifecycle };
  }

  if (deriveOverdue(m, today)) {
    return { lifecycle, riskOverlay: "late" };
  }

  if (lifecycle === "upcoming" && isSet(m.plannedDate)) {
    const todayResolved = today ?? getBrisbaneToday();
    const daysUntil = daysBetween(todayResolved, m.plannedDate);
    if (!Number.isNaN(daysUntil) && daysUntil >= 0 && daysUntil <= DUE_SOON_DAYS) {
      return { lifecycle, riskOverlay: "due-soon" };
    }
  }

  return { lifecycle };
}
